import React, { useEffect, useMemo, useState } from 'react'
import { ProductData, ProductVariant, Product } from '@interfaces/product'
import VariantSelector from './VariantSelector'
import CoreFeatures from './CoreFeatures'
import DeliveryDate from './DeliveryDate'
import ProductValueProps from './ProductValueProps'
import ProductDrawers from './ProductDrawers'
import LensTechSection from './LensTechSection'
import { formatMoney } from '@helpers/global'
import SimilarVariants from './SimilarVariants'
import AddToCartButton from './AddToCartButton'

const ProductForm: React.FC<ProductData> = ({ product, children, loyaltyEnabled, deliveryDateEnabled }) => {
  // Lookup variant based on selected options
  const [currentVariant, setCurrentVariant] = useState<ProductVariant | undefined>(
    product.variants?.find(
      (v: ProductVariant) =>
        JSON.stringify(v.selectedOptions) === JSON.stringify(product.variantBySelectedOptions?.selectedOptions),
    ),
  )

  const SimilarVariantsButton: React.FC<{product: Product}>=({product}) => {
    const [isSimilarVariantsDrawerOpen, setIsSimilarVariantsDrawerOpen] = useState<boolean>(false);
    const handleCloseSimilarVariantsDrawer: () => void = () => setIsSimilarVariantsDrawerOpen(false)
    const isLensType = product.options?.find((option)=> option.name.toLowerCase() === 'lens type')

    if(isLensType && isLensType.values.length === 1) {
      return(
        <>
          <a className="button button--secondary w-full md:w-auto" onClick={() => setIsSimilarVariantsDrawerOpen(true)}>
            <div className="rectangle-1"></div>
            <div className="button-text">{window.DEPT.Strings.product.viewSimilarVariants}</div>
          </a>
          <SimilarVariants product={product} isOpen={isSimilarVariantsDrawerOpen}
                       onClose={handleCloseSimilarVariantsDrawer} />
        </>
      )
    }
  }

  const variantSelectors = useMemo(() => {
    if (!product.hasOnlyDefaultVariant && product.options) {
      return product.options.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          variants={product.variants || []}
          product={product}
        />
      ))
    }
    return null
  }, [product, currentVariant])

  const ProductActions: React.FC<{ currentVariant: ProductVariant }> = ({ currentVariant }) => {
    const quantity = 1;
    // const [quantity, setQuantity] = useState(1)
    let currentPrice = 0;
    //Calculate Pitcoin amount based on if the product is on sale when the sale price comes from a metafield
    if (currentVariant.saleDiscount){
      const variantDiscount = Number((100 - currentVariant.saleDiscount) / 100);
      currentPrice = Number(currentVariant.price.amount * variantDiscount)
    } else {
      currentPrice = Number(currentVariant.price.amount)
    }
    const pitcoins = Math.round(Number(currentPrice * quantity * 5))
    const dollarValue = Number((currentPrice * quantity) * 0.05).toFixed(2)
    const dollarValueWithCurrencySign = formatMoney({ amount: String(dollarValue) }) || ''
    const pitCoinCopy = (
      <>
        <a href="/account" className="underline">Sign up</a>&nbsp;
        {`to earn ${pitcoins} Pitcoin™ (That's ${dollarValueWithCurrencySign})`}
      </>
    )
    const dollarValueInstalment = Number((currentVariant.price.amount * quantity) / 4).toFixed(2)
    const dollarValueInstalmentWithCurrencySign = formatMoney({ amount: String(dollarValueInstalment) }) || ''
    const instalmentsCopy = children
      ? children
      : `Pay in 4 interest-free instalments of ${dollarValueInstalmentWithCurrencySign} with Shop Pay`
    return (
      <>
        {/*<div className="grid grid-cols-1 pb-6">*/}
          {/*{!comingSoonProduct && (*/}
          {/*  <ProductQuantity quantity={quantity} setQuantity={setQuantity} />*/}
          {/*)}*/}
        {/*</div>*/}
        <div className="flex body-xxs">{instalmentsCopy}</div>
        {deliveryDateEnabled && (
          <DeliveryDate currentVariant={currentVariant} />
        )}

        <div className="flex py-4 justify-center md:justify-start">
          <AddToCartButton product={product} currentVariant={currentVariant} quantity={quantity} />
        </div>
        <div className="flex pb-4 justify-center md:justify-start">
          <SimilarVariantsButton product={product}/>
        </div>
        {loyaltyEnabled && currentVariant.availableForSale && (
          <div className="flex body-xxs">{pitCoinCopy}</div>
        )}
      </>
    )
  }

  const productActions = useMemo(() => {
    return currentVariant ? <ProductActions currentVariant={currentVariant} /> : null
  }, [currentVariant])

  useEffect(() => {
    if (currentVariant) {
      const searchParams = new URLSearchParams(window.location.search)
      const id = currentVariant.id.toString().split('/ProductVariant/')[1]
      const firstShopayBannerElement = document.getElementsByTagName('shopify-payment-terms')[0]
      if (firstShopayBannerElement) {
        firstShopayBannerElement.setAttribute('variant-id', id)
      }
      searchParams.set('variant', id)
      const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`
      window.history.replaceState(null, '', newRelativePathQuery)
    }
  }, [currentVariant])

  const exclusiveProduct = currentVariant?.metafields?.badges.find((badge) => badge.name.includes('Exclusive'));
  const exclusiveRule = exclusiveProduct && !currentVariant?.customerLoggedIn;

  const innerContent = () => {
    return (
        <div>
          <div>
            <div className="hidden md:block">
              {currentVariant?.coreFeatures &&
                Array.isArray(currentVariant.coreFeatures.blocks) &&
                currentVariant.coreFeatures.blocks.length > 0 &&
                currentVariant.coreFeatures.blocks.every((block) => typeof block === 'object' && block !== null) && (
                  <CoreFeatures
                    title={currentVariant.coreFeatures.title}
                    blocks={currentVariant.coreFeatures.blocks}
                    className={currentVariant.coreFeatures.className}
                  />
                )}
            </div>
            {variantSelectors}
            <div className="grid grid-cols-1">{productActions}</div>
            <div className="block md:hidden mt-6 md:mt-0">
              {currentVariant?.coreFeatures &&
                Array.isArray(currentVariant.coreFeatures.blocks) &&
                currentVariant.coreFeatures.blocks.length > 0 &&
                currentVariant.coreFeatures.blocks.every((block) => typeof block === 'object' && block !== null) && (
                  <CoreFeatures
                    title={currentVariant.coreFeatures.title}
                    blocks={currentVariant.coreFeatures.blocks}
                    className={currentVariant.coreFeatures.className}
                  />
                )}
            </div>
            {currentVariant?.valueProps && (
              <ProductValueProps
                title={currentVariant.valueProps.title}
                className={currentVariant.valueProps.className}
                blocks={currentVariant.valueProps.blocks}
              />
            )}

            {currentVariant?.lensTechSection && currentVariant?.lensTechSection.lensImage.src && (
              <LensTechSection {...currentVariant.lensTechSection} />
            )}

            {currentVariant?.accordionData && (
              <ProductDrawers
                componentClasses={currentVariant.accordionData.componentClasses}
                variantAccordionBlocks={currentVariant.accordionData.variantAccordionBlocks}
                accordionBlocks={currentVariant.accordionData.accordionBlocks}
                witbBlocks={currentVariant.accordionData.witbBlocks}
              />
            )}
          </div>
        </div>
    )
  }
  return (
    <>
      {
        !exclusiveRule ? (
          <form method="post" action="/cart/add" acceptCharset="UTF-8" encType="multipart/form-data" noValidate>
            {innerContent()}
          </form>
        ) : (
          <>{innerContent()}</>
        )
      }
    </>

  )
}

export default ProductForm
